import AxiosInstance from "../../shared/utils/axios";

/**
 * Fetches trials data based on the provided parameters.
 * @param orgId The ID of the organization.
 * @param farmId The ID of the farm.
 * @param page The page number for pagination.
 * @param limit The number of items per page.
 * @param search The search query.
 * @param status The status filter.
 * @returns trials data
 */
export const fetchTrials = async (
  orgId: string,
  farmId: string,
  page: number = 1,
  limit: number = 10,
  search: string = "",
  status: string = ""
): Promise<any> => {
  const filters: Record<string, string> = {
    org_id: orgId,
    property_id: farmId,
    page: String(page),
    limit: String(limit),
    search,
    paginated: "true",
  };

  if (status === "completed") {
    filters.completion = "true";
  } else if (status === "incomplete") {
    filters.completion = "false";
  }

  const params = new URLSearchParams(filters);
  const response = await AxiosInstance.get(`/trials?${params.toString()}`);

  return response.data;
};

/**
 * fetches the latest details of a single trial.
 * @param trialId The ID of the trial to fetch.
 * @returns the trial details for the given trial ID.
 */
export const fetchTrialById = async (trialId: string): Promise<any> => {
  const response = await AxiosInstance.get(`/trial/${trialId}`);
  return response.data;
};

/**
 * generates a report for the specified trial IDs and field IDs.
 * @param trialId The IDs of the trials for which the report is to be generated.
 * @param fieldIds The IDs of the fields for which the report is to be generated.
 * @returns A promise that resolves to an object containing the UUID and report ID of the generated report.
 */
export const generateReport = async (
  trialId: string[],
  fieldIds: string[]
): Promise<{ uuid: string; reportId: string }> => {
  const response = await AxiosInstance.post("/online_report/generate", {
    trial_ids: trialId,
    field_ids: fieldIds,
  });
  const uuid = response.data?.UUID || response.data?.uuid;
  const reportId = response.data?.report_id;
  if (!uuid || !reportId) {
    throw new Error("No report UUID or reportId received in response");
  }
  const data = { uuid, reportId };
  return data;
};

/**
 * deletes a trial with the specified trial ID.
 * @param trialId The ID of the trial to be deleted.
 */
export const deleteTrial = async (trialId: string): Promise<void> => {
  await AxiosInstance.delete(`/trial/${trialId}`);
};

/**
 * marks a trial as complete based on the provided trial ID.
 * @param trialId The ID of the trial to be marked as complete.
 * @param checked Indicates whether the trial is complete.
 */
export const markTrialComplete = async (
  trialId: string,
  checked: boolean
): Promise<any> => {
  const response = await AxiosInstance.patch(`/trial/${trialId}`, {
    completion: checked,
  });
  return response.data;
};
